import React, { useState, useImperativeHandle, forwardRef, useEffect, useMemo, useCallback, useRef } from 'react';
import { faCheckCircle, faExclamationTriangle, faBriefcase, faMapMarkerAlt, faUserTie } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from '../../../../../services/api';
import { formatNumber, parseLocaleNumber } from '../../../../../utils/numberFormatter';
import Swal from 'sweetalert2';
import { useSuggestedData } from '../../../../../context/SuggestedDataContext';
import { Modal } from 'react-bootstrap';
import { DataSourceBadge, EditButton } from '../../../../Common';
import { Button, PhoneField } from '../../../../ui';
import { normalizeEmploymentId } from '../../../../../utils/employment';
import './EmploymentInfo.css';

// Configuración para selección automática de datos de bureaus
const PRIORITIZED_DATA_SOURCES = ['siisa']; // Lista de sources priorizados, ordenados por prioridad
const RENEWER_CLIENT_TYPE = 'RENEWER';

const provinces = [{"id":2,"name":"Buenos Aires"},{"id":3,"name":"Capital Federal"},{"id":10,"name":"Catamarca"},{"id":8,"name":"Chaco"},{"id":19,"name":"Chubut"},{"id":22,"name":"Cordoba"},{"id":21,"name":"Corrientes"},{"id":5,"name":"Entre Rios"},{"id":14,"name":"Formosa"},{"id":23,"name":"Jujuy"},{"id":11,"name":"La Pampa"},{"id":6,"name":"La Rioja"},{"id":12,"name":"Mendoza"},{"id":13,"name":"Misiones"},{"id":15,"name":"Neuquen"},{"id":16,"name":"Rio Negro"},{"id":1,"name":"Salta"},{"id":9,"name":"San Juan"},{"id":4,"name":"San Luis"},{"id":24,"name":"Santa Cruz"},{"id":17,"name":"Santa Fe"},{"id":7,"name":"Santiago del Estero"},{"id":20,"name":"Tierra del Fuego"},{"id":18,"name":"Tucuman"}];

const isLocalEmploymentSuggestion = (suggestion) => {
  const suggestionId = suggestion?.id?.toString() || '';
  return suggestionId.startsWith('new_') || suggestionId.startsWith('edited_');
};

export const normalizeEmploymentSuggestion = (suggestion) => {
  const isLocal = isLocalEmploymentSuggestion(suggestion);
  const employer = suggestion?.employer || {};
  const job = suggestion?.job || {};
  const address = suggestion?.address || {};
  const normalizedCuit = String(employer.cuit || employer.cuit_formatted || '').replace(/\D/g, '');

  return {
    id: suggestion?.id,
    isLocal,
    companyName: employer.name || '',
    companyIdentification: normalizedCuit,
    cuit: normalizedCuit,
    position: job.title || '',
    entryDate: job.entry_date || job.join_date || '',
    workPhone: employer.phone || '',
    workAddress: isLocal ? address.street : employer.address,
    workPostalCode: isLocal ? address.postal_code : employer.postal_code,
    provinceId: isLocal ? address.province : employer.province_id,
    provinceName: isLocal ? address.province_label : employer.province,
    localityId: isLocal ? address.locality : employer.locality_id,
    localityName: isLocal ? address.locality_label : employer.locality
  };
};

const normalizeCuit = (value) => String(value || '').replace(/\D/g, '');

const LABOR_FORM_FIELDS = [
  'companyName',
  'companyIdentification',
  'position',
  'entryDate',
  'workPhone',
  'workAddress',
  'workProvince',
  'workLocality',
  'workPostalCode'
];

const pickLaborFormFields = (formData = {}) => LABOR_FORM_FIELDS.reduce((snapshot, field) => ({
  ...snapshot,
  [field]: formData[field] || ''
}), {});

const normalizeName = (value) => String(value || '').trim().toLowerCase();

const hasValue = (value) => value !== undefined && value !== null && value !== '';

export const resolveProvinceId = (normalizedSuggestion, availableProvinces = []) => {
  if (hasValue(normalizedSuggestion?.provinceId)) {
    return Number(normalizedSuggestion.provinceId);
  }

  const provinceName = normalizeName(normalizedSuggestion?.provinceName);
  if (!provinceName) {
    return '';
  }

  const province = availableProvinces.find((item) => normalizeName(item.name) === provinceName);
  return province ? province.id : '';
};

export const resolveLocalityId = (normalizedSuggestion, localities = []) => {
  if (hasValue(normalizedSuggestion?.localityId)) {
    const localityById = localities.find((item) => Number(item.id) === Number(normalizedSuggestion.localityId));
    if (localityById) {
      return localityById.id;
    }
  }

  const localityName = normalizeName(normalizedSuggestion?.localityName);
  if (!localityName) {
    return '';
  }

  const locality = localities.find((item) => normalizeName(item.name) === localityName);
  return locality ? locality.id : '';
};

export const EMPLOYMENT_REQUIRED_FIELD_LABELS = {
  companyName: 'Empresa',
  companyIdentification: 'CUIT empleador',
  entryDate: 'Fecha de ingreso',
  workAddress: 'Domicilio laboral',
  workProvince: 'Provincia',
  workLocality: 'Localidad',
  workPostalCode: 'Código postal laboral'
};

export const getEmploymentRequiredFieldValuesFromSuggestion = (suggestion, availableProvinces = provinces) => {
  const normalized = normalizeEmploymentSuggestion(suggestion);
  const provinceId = resolveProvinceId(normalized, availableProvinces);
  const hasResolvedProvince = hasValue(provinceId) || hasValue(normalized.provinceName);
  const hasResolvedLocality = hasValue(normalized.localityId) || hasValue(normalized.localityName);

  return {
    companyName: normalized.companyName,
    companyIdentification: normalized.companyIdentification,
    entryDate: normalized.entryDate,
    workAddress: normalized.workAddress,
    workProvince: hasResolvedProvince ? (provinceId || normalized.provinceName) : '',
    workLocality: hasResolvedLocality ? (normalized.localityId || normalized.localityName) : '',
    workPostalCode: normalized.workPostalCode
  };
};

export const isEmploymentDataIncomplete = (employmentData) => {
  const cuit = String(employmentData?.companyIdentification || employmentData?.cuit || '').replace(/\D/g, '');

  return !(
    employmentData?.companyName &&
    cuit.length === 11 &&
    employmentData?.entryDate &&
    employmentData?.workAddress &&
    employmentData?.workProvince &&
    employmentData?.workLocality &&
    employmentData?.workPostalCode
  );
};

const hasMinimumSelectedEmploymentData = (employmentData) => !isEmploymentDataIncomplete(employmentData);

export const computeEmploymentSectionValidity = ({
  formData,
  selectedEmploymentId,
  isTargetInvalid
}) => {
  if (isTargetInvalid) {
    return false;
  }

  if (selectedEmploymentId) {
    return hasMinimumSelectedEmploymentData({
      companyName: formData?.companyName,
      companyIdentification: formData?.companyIdentification,
      entryDate: formData?.entryDate,
      workAddress: formData?.workAddress,
      workProvince: formData?.workProvince,
      workLocality: formData?.workLocality,
      workPostalCode: formData?.workPostalCode
    });
  }

  return null;
};

export const isSiisaEmploymentSuggestion = (suggestion) =>
  suggestion?.source?.toLowerCase() === 'siisa';

export const isEmploymentSuggestionIncomplete = (suggestion, availableProvinces = provinces) =>
  isEmploymentDataIncomplete(getEmploymentRequiredFieldValuesFromSuggestion(suggestion, availableProvinces));

export const shouldShowSiisaIncompleteBadge = (suggestion, availableProvinces = provinces) =>
  isSiisaEmploymentSuggestion(suggestion) && isEmploymentSuggestionIncomplete(suggestion, availableProvinces);

export const getMissingEmploymentFieldLabels = (suggestion, availableProvinces = provinces) => {
  const values = getEmploymentRequiredFieldValuesFromSuggestion(suggestion, availableProvinces);
  const missing = [];

  if (!values.companyName) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.companyName);
  const cuit = String(values.companyIdentification || '').replace(/\D/g, '');
  if (cuit.length !== 11) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.companyIdentification);
  if (!values.entryDate) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.entryDate);
  if (!values.workAddress) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.workAddress);
  if (!values.workProvince) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.workProvince);
  if (!values.workLocality) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.workLocality);
  if (!values.workPostalCode) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.workPostalCode);

  return missing;
};

export const getMissingEmploymentFieldLabelsFromFormData = (formData) => {
  const missing = [];

  if (!formData?.companyName) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.companyName);
  const cuit = String(formData?.companyIdentification || '').replace(/\D/g, '');
  if (cuit.length !== 11) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.companyIdentification);
  if (!formData?.entryDate) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.entryDate);
  if (!formData?.workAddress) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.workAddress);
  if (!formData?.workProvince) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.workProvince);
  if (!formData?.workLocality) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.workLocality);
  if (!formData?.workPostalCode) missing.push(EMPLOYMENT_REQUIRED_FIELD_LABELS.workPostalCode);

  return missing;
};

const EmploymentInfo = forwardRef((props, ref) => {
  const {
    formData,
    handleChange,
    activeSection,
    sectionName,
    setSectionValidity,
    sectionValidity,
    handleSectionClick,
    isLoadingSuggestions,
    displayMode = 'accordion',
    clientData,
    targetEmployment = null,
    setFormData
  } = props;

  const targetLaborDataId = normalizeEmploymentId(targetEmployment?.labor_data_id);
  const isTargetEmploymentLocked = Boolean(targetLaborDataId);

  const isContinuousMode = displayMode === 'continuous';

  const { suggestedData, selectedData, setSelectedData, setSuggestedData } = useSuggestedData();
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [workLocalities, setWorkLocalities] = useState([]);
  const [isLoadingLocalities, setIsLoadingLocalities] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [showNewEmploymentModal, setShowNewEmploymentModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isEditingEmployment, setIsEditingEmployment] = useState(false);
  const [editingEmploymentData, setEditingEmploymentData] = useState(null);
  const targetHydratedRef = useRef(null);
  const isMountedRef = useRef(true);
  const formDataRef = useRef(formData);
  const employmentEditSnapshotRef = useRef(null);

  const suggestions = useMemo(() => suggestedData.employment || [], [suggestedData.employment]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  // Filtrar duplicados, sugerencias reemplazadas y ordenar por fecha (más reciente primero)
  const uniqueSuggestions = useMemo(() => suggestions
    .filter((sugg, idx, arr) => arr.findIndex(s => normalizeEmploymentId(s.id) === normalizeEmploymentId(sugg.id)) === idx)
    .filter(sugg => !sugg.replacedBy) // Ocultar sugerencias que han sido reemplazadas
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)), [suggestions]);

  const targetSuggestion = useMemo(() => {
    if (!targetLaborDataId) {
      return null;
    }

    const matchingSuggestion = uniqueSuggestions.find(
      (suggestion) => normalizeEmploymentId(suggestion.id) === targetLaborDataId
    );
    if (matchingSuggestion) {
      return matchingSuggestion;
    }

    const cuit = normalizeCuit(targetEmployment?.company_identification);
    if (!cuit) {
      return null;
    }

    return {
      id: targetLaborDataId,
      employer: {
        name: '',
        cuit
      },
      job: {
        join_date: targetEmployment?.join_date || ''
      },
      source: 'linked',
      created_at: new Date().toISOString(),
      isTargetFallback: true
    };
  }, [targetLaborDataId, targetEmployment?.company_identification, targetEmployment?.join_date, uniqueSuggestions]);

  const targetCuit = normalizeCuit(targetEmployment?.company_identification)
    || normalizeCuit(targetSuggestion?.employer?.cuit)
    || normalizeCuit(targetSuggestion?.employer?.cuit_formatted);
  const isTargetInvalid = isTargetEmploymentLocked && !targetCuit;

  const hasManualEmploymentInput = useMemo(() => {
    const employmentFields = [
      'companyName',
      'companyIdentification',
      'position',
      'entryDate',
      'workPhone',
      'workAddress',
      'workProvince',
      'workLocality',
      'workPostalCode'
    ];

    return employmentFields.some((field) => {
      const value = formData?.[field];
      return value !== undefined && value !== null && String(value).trim() !== '';
    });
  }, [formData]);

  // Función para auto-seleccionar datos del bureau priorizado para clientes renovadores
  const autoSelectPrioritizedData = useCallback(() => {
    if (isTargetEmploymentLocked) {
      return;
    }

    // Solo aplicar para clientes renovadores
    if (clientData?.client_type?.code !== RENEWER_CLIENT_TYPE) {
      return;
    }

    // Solo aplicar si no hay nada seleccionado aún
    if (selectedData.employment) {
      return;
    }

    if (showNewEmploymentModal || isEditingEmployment || hasManualEmploymentInput) {
      return;
    }

    // Buscar el dato más reciente entre todos los sources priorizados
    const prioritizedSuggestions = suggestions
      .filter(suggestion => {
        // Verificar si el dato viene de algún source priorizado
        return PRIORITIZED_DATA_SOURCES.some(source => 
          suggestion.source?.toLowerCase() === source.toLowerCase()
        );
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const mostRecentPrioritized = prioritizedSuggestions[0];
    
    if (mostRecentPrioritized) {
      handleUseExisting(mostRecentPrioritized);
    }
  }, [
    clientData?.client_type?.code,
    selectedData.employment,
    showNewEmploymentModal,
    isEditingEmployment,
    hasManualEmploymentInput,
    suggestions,
    isTargetEmploymentLocked
  ]);

  // Obtener las sugerencias a mostrar
  const displayedSuggestions = useMemo(() => {
    if (isTargetEmploymentLocked) {
      return targetSuggestion && !isTargetInvalid ? [targetSuggestion] : [];
    }

    return showAllSuggestions
      ? uniqueSuggestions
      : uniqueSuggestions.filter((suggestion, index) =>
        index === 0 || // Siempre mostrar la más reciente
        suggestion.id.toString().startsWith('new_') || // Mostrar las nuevas si hay ocultas
        suggestion.id.toString().startsWith('edited_') // Mostrar las editadas
      );
  }, [isTargetEmploymentLocked, targetSuggestion, isTargetInvalid, showAllSuggestions, uniqueSuggestions]);

  const requiredFields = [
    'companyName',
    'companyIdentification',
    'entryDate',
    'workAddress',
    'workProvince',
    'workLocality',
    'workPostalCode'
  ];

  useEffect(() => {
    if (activeSection === sectionName && (selectedData.employment || touchedFields.companyName)) {
      validateSection();
    }
  }, [activeSection, sectionName, selectedData.employment]);

  useEffect(() => {
    if (activeSection === sectionName && suggestions !== undefined && suggestions !== null) {
      setShowNewForm(!isTargetEmploymentLocked && suggestions.length === 0);
    }
  }, [activeSection, sectionName, suggestions, isTargetEmploymentLocked]);

  // UseEffect para auto-seleccionar datos del bureau priorizado
  useEffect(() => {
    if (suggestions.length > 0 && clientData) {
      autoSelectPrioritizedData();
    }
  }, [suggestions, clientData, autoSelectPrioritizedData]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    handleChange(e);
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'companyName':
        if (!value) {
          error = 'El nombre de la empresa es requerido';
        }
        break;
      case 'companyIdentification': {
        const digits = String(value || '').replace(/\D/g, '');
        if (!digits) {
          error = 'El CUIT del empleador es requerido';
        } else if (digits.length !== 11) {
          error = 'El CUIT del empleador debe tener 11 dígitos';
        }
        break;
      }
      case 'entryDate':
        if (!value) {
          error = 'La fecha de ingreso es requerida';
        } else if (new Date(value) > new Date()) {
          error = 'Ingrese una fecha de ingreso válida';
        }
        break;
      case 'workAddress':
        if (!value) {
          error = 'El domicilio laboral es requerido';
        }
        break;
      case 'workProvince':
        if (!value) {
          error = 'La provincia es requerida';
        }
        break;
      case 'workLocality':
        if (!value) {
          error = 'La localidad es requerida';
        }
        break;        
      case 'workPostalCode':
        if (!value) {
          error = 'El código postal laboral es requerido';
        }
        break;
      default:
        break;
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
    return error;
  };

  const isLaborFieldEmpty = (field) => {
    const value = formDataRef.current?.[field];
    return value === undefined || value === null || String(value).trim() === '';
  };

  const setLaborFieldIfAllowed = (field, value, mergeOnly = false) => {
    if (!isMountedRef.current) {
      return;
    }

    if (mergeOnly && !isLaborFieldEmpty(field)) {
      return;
    }

    handleChange({ target: { name: field, value: value || '' } });
  };

  const applyEmploymentSuggestion = async (normalizedSuggestion, { mergeOnly = false } = {}) => {
    const provinceId = resolveProvinceId(normalizedSuggestion, provinces);

    setLaborFieldIfAllowed('companyName', normalizedSuggestion.companyName, mergeOnly);
    setLaborFieldIfAllowed('companyIdentification', normalizedSuggestion.companyIdentification, mergeOnly);
    setLaborFieldIfAllowed('position', normalizedSuggestion.position, mergeOnly);
    setLaborFieldIfAllowed('entryDate', normalizedSuggestion.entryDate, mergeOnly);
    setLaborFieldIfAllowed('workPhone', normalizedSuggestion.workPhone, mergeOnly);
    setLaborFieldIfAllowed('workAddress', normalizedSuggestion.workAddress, mergeOnly);
    setLaborFieldIfAllowed('workPostalCode', normalizedSuggestion.workPostalCode, mergeOnly);
    setLaborFieldIfAllowed('workProvince', provinceId, mergeOnly);
    if (!mergeOnly) {
      setLaborFieldIfAllowed('workLocality', '', false);
    }

    if (!provinceId) {
      if (isMountedRef.current && !mergeOnly) {
        setWorkLocalities([]);
      }
      return { provinceId: '', localityId: '' };
    }

    const loadedLocalities = await loadLocalitiesByProvince(provinceId);
    if (!isMountedRef.current) {
      return { provinceId, localityId: '' };
    }

    const localityId = resolveLocalityId(normalizedSuggestion, loadedLocalities);
    setLaborFieldIfAllowed('workLocality', localityId, mergeOnly);

    return { provinceId, localityId };
  };

  useEffect(() => {
    if (!isTargetEmploymentLocked) {
      return;
    }

    if (normalizeEmploymentId(selectedData.employment) !== targetLaborDataId) {
      setSelectedData((prev) => ({ ...prev, employment: targetLaborDataId }));
    }

    const fallbackCuit = normalizeCuit(targetEmployment?.company_identification);
    if (fallbackCuit && isLaborFieldEmpty('companyIdentification')) {
      setLaborFieldIfAllowed('companyIdentification', fallbackCuit, true);
    }

    if (targetEmployment?.join_date && isLaborFieldEmpty('entryDate')) {
      setLaborFieldIfAllowed('entryDate', targetEmployment.join_date, true);
    }

    if (!targetSuggestion || targetSuggestion.isTargetFallback || isTargetInvalid) {
      return;
    }

    const hydrateKey = `${targetLaborDataId}:${normalizeEmploymentId(targetSuggestion.id)}`;
    if (targetHydratedRef.current === hydrateKey) {
      return;
    }

    targetHydratedRef.current = hydrateKey;
    Promise.resolve(
      applyEmploymentSuggestion(normalizeEmploymentSuggestion(targetSuggestion), { mergeOnly: true })
    ).catch(() => {
      if (targetHydratedRef.current === hydrateKey) {
        targetHydratedRef.current = null;
      }
    });
  }, [
    isTargetEmploymentLocked,
    selectedData.employment,
    targetLaborDataId,
    targetEmployment?.company_identification,
    targetEmployment?.join_date,
    targetSuggestion,
    isTargetInvalid,
    setSelectedData
  ]);

  const handleUseExisting = async (suggestion) => {
    const suggestionId = normalizeEmploymentId(suggestion?.id);
    if (isTargetEmploymentLocked && suggestionId !== targetLaborDataId) {
      return;
    }

    const normalizedSuggestion = normalizeEmploymentSuggestion(suggestion);

    setSelectedData(prev => ({
      ...prev,
      employment: isTargetEmploymentLocked ? targetLaborDataId : suggestion.id
    }));

    const resolvedLocation = await applyEmploymentSuggestion(normalizedSuggestion, { mergeOnly: isTargetEmploymentLocked });
    const selectedEmploymentData = {
      ...normalizedSuggestion,
      workProvince: resolvedLocation.provinceId || '',
      workLocality: resolvedLocation.localityId || ''
    };

    setSectionValidity(prev => ({
      ...prev,
      [sectionName]: hasMinimumSelectedEmploymentData(selectedEmploymentData)
    }));
    setShowNewForm(false);
  };

  const handleUseNewForm = () => {
    if (isTargetEmploymentLocked) {
      return;
    }

    setFormSubmitted(false);
    setIsEditingEmployment(false);
    setEditingEmploymentData(null);
    
    // Limpiamos la selección
    setSelectedData(prev => ({
      ...prev,
      employment: null
    }));
    
    // Limpiamos el formulario
    handleChange({ target: { name: 'companyName', value: '' } });
    handleChange({ target: { name: 'companyIdentification', value: '' } });
    handleChange({ target: { name: 'position', value: '' } });
    handleChange({ target: { name: 'entryDate', value: '' } });
    handleChange({ target: { name: 'workPhone', value: '' } });
    handleChange({ target: { name: 'workAddress', value: '' } });
    handleChange({ target: { name: 'workProvince', value: '' } });
    handleChange({ target: { name: 'workLocality', value: '' } });
    handleChange({ target: { name: 'workPostalCode', value: '' } });
    
    // Limpiamos los campos tocados
    setTouchedFields({});
    
    // Limpiamos los errores
    setErrors({});
    
    // Limpiamos las localidades
    setWorkLocalities([]);
    
    setShowNewEmploymentModal(true);
  };

  const handleEditEmployment = async (suggestion) => {
    const suggestionId = normalizeEmploymentId(suggestion?.id);
    if (isTargetEmploymentLocked && suggestionId !== targetLaborDataId) {
      return;
    }

    if (isTargetEmploymentLocked) {
      employmentEditSnapshotRef.current = pickLaborFormFields(formDataRef.current);
    }

    setIsEditingEmployment(true);
    setEditingEmploymentData(suggestion);
    setShowNewEmploymentModal(true);
    setFormSubmitted(false);

    const normalizedSuggestion = normalizeEmploymentSuggestion(suggestion);
    await applyEmploymentSuggestion(normalizedSuggestion, { mergeOnly: isTargetEmploymentLocked });
    
    setErrors({});
    setTouchedFields({});
  };

  const handleCloseModal = () => {
    if (isTargetEmploymentLocked && employmentEditSnapshotRef.current) {
      const restoredFormData = {
        ...formDataRef.current,
        ...employmentEditSnapshotRef.current
      };
      formDataRef.current = restoredFormData;
      setFormData(restoredFormData);
      employmentEditSnapshotRef.current = null;
      setFormSubmitted(false);
      setShowNewEmploymentModal(false);
      setIsEditingEmployment(false);
      setEditingEmploymentData(null);
      setErrors({});
      setTouchedFields({});
      return;
    }

    setFormSubmitted(false);
    setShowNewEmploymentModal(false);
    setIsEditingEmployment(false);
    setEditingEmploymentData(null);
    
    // Resetear todos los campos
    handleChange({ target: { name: 'companyName', value: '' } });
    handleChange({ target: { name: 'companyIdentification', value: '' } });
    handleChange({ target: { name: 'position', value: '' } });
    handleChange({ target: { name: 'entryDate', value: '' } });
    handleChange({ target: { name: 'workAddress', value: '' } });
    handleChange({ target: { name: 'workProvince', value: '' } });
    handleChange({ target: { name: 'workLocality', value: '' } });
    handleChange({ target: { name: 'workPostalCode', value: '' } });
    handleChange({ target: { name: 'workPhone', value: '' } });
    setErrors({});
    setTouchedFields({});
    setWorkLocalities([]);
  };

  const checkForDuplicates = () => {
    const normalizedCompanyName = String(formData.companyName).trim().toLowerCase();
    const normalizedPosition = String(formData.position || '').trim().toLowerCase();
    const normalizedWorkAddress = String(formData.workAddress).trim().toLowerCase();
    const normalizedProvince = provinces.find(p => p.id === Number(formData.workProvince))?.name.trim().toLowerCase();
    const normalizedLocality = workLocalities.find(l => l.id === Number(formData.workLocality))?.name.trim().toLowerCase();
    const normalizedPostalCode = String(formData.workPostalCode).trim().toLowerCase();

    return suggestions.some(suggestion => {
      const existingCompanyName = String(suggestion.employer?.name || '').trim().toLowerCase();
      const existingPosition = String(suggestion.job?.title || '').trim().toLowerCase();
      const existingWorkAddress = String(suggestion.address?.street || '').trim().toLowerCase();
      const existingProvince = String(suggestion.address?.province || '').trim().toLowerCase();
      const existingLocality = String(suggestion.address?.locality || '').trim().toLowerCase();
      const existingPostalCode = String(suggestion.address?.postal_code || '').trim().toLowerCase();

      const isMatch = existingCompanyName === normalizedCompanyName &&
                     existingPosition === normalizedPosition &&
                     existingWorkAddress === normalizedWorkAddress &&
                     existingProvince === normalizedProvince &&
                     existingLocality === normalizedLocality &&
                     existingPostalCode === normalizedPostalCode;

      return isMatch;
    });
  };

  const buildEditedTargetSuggestion = (baseSuggestion = {}) => {
    const selectedProvince = provinces.find(p => p.id === Number(formData.workProvince));
    const selectedLocality = workLocalities.find(l => l.id === Number(formData.workLocality));
    const cuit = normalizeCuit(formData.companyIdentification || targetCuit);

    return {
      ...baseSuggestion,
      id: targetLaborDataId,
      employer: {
        ...(baseSuggestion.employer || {}),
        name: formData.companyName,
        cuit,
        phone: formData.workPhone || '',
        address: formData.workAddress,
        postal_code: formData.workPostalCode,
        province: selectedProvince?.name || baseSuggestion.employer?.province || '',
        locality: selectedLocality?.name || baseSuggestion.employer?.locality || '',
        province_id: formData.workProvince || baseSuggestion.employer?.province_id || '',
        locality_id: formData.workLocality || baseSuggestion.employer?.locality_id || ''
      },
      job: {
        ...(baseSuggestion.job || {}),
        title: formData.position || '',
        join_date: formData.entryDate,
        entry_date: formData.entryDate
      },
      address: {
        ...(baseSuggestion.address || {}),
        street: formData.workAddress,
        province: formData.workProvince || '',
        province_label: selectedProvince?.name || '',
        locality: formData.workLocality || '',
        locality_label: selectedLocality?.name || '',
        postal_code: formData.workPostalCode
      },
      source: baseSuggestion.source || 'linked',
      created_at: baseSuggestion.created_at || new Date().toISOString(),
      isTargetFallback: baseSuggestion.isTargetFallback || false
    };
  };

  const handleSaveTargetEmploymentEdit = () => {
    setFormSubmitted(true);
    const isValid = validateSection();
    if (!isValid) {
      return;
    }

    const nextTargetSuggestion = buildEditedTargetSuggestion(targetSuggestion || editingEmploymentData);

    setSelectedData(prev => ({
      ...prev,
      employment: targetLaborDataId
    }));

    setSuggestedData(prev => {
      const employment = prev.employment || [];
      const hasTarget = employment.some((item) => normalizeEmploymentId(item.id) === targetLaborDataId);
      const nextEmployment = hasTarget
        ? employment.map((item) => (
          normalizeEmploymentId(item.id) === targetLaborDataId
            ? buildEditedTargetSuggestion(item)
            : item
        ))
        : [nextTargetSuggestion, ...employment];

      return {
        ...prev,
        employment: nextEmployment
      };
    });

    employmentEditSnapshotRef.current = null;
    setShowNewEmploymentModal(false);
    setIsEditingEmployment(false);
    setEditingEmploymentData(null);
    setErrors({});
    setTouchedFields({});
    setSectionValidity((prev) => ({
      ...prev,
      [sectionName]: computeEmploymentSectionValidity({
        formData,
        selectedEmploymentId: targetLaborDataId,
        isTargetInvalid
      })
    }));
  };

  const handleSaveNewEmployment = () => {
    if (isTargetEmploymentLocked && isEditingEmployment) {
      handleSaveTargetEmploymentEdit();
      return;
    }

    setFormSubmitted(true);
    // Validar el formulario antes de guardar
    const isValid = validateSection();
    if (!isValid) {
      return;
    }

    // Check for duplicates solo si no estamos editando
    if (!isEditingEmployment) {
      const isDuplicate = checkForDuplicates();
      if (isDuplicate) {
        Swal.fire({
          icon: 'error',
          title: 'Empleo duplicado',
          text: 'Este empleo ya existe en el sistema.',
          confirmButtonText: 'Entendido'
        });
        return;
      }
    }

    // Obtener los nombres correctos de provincia y localidad
    const selectedProvince = provinces.find(p => p.id === Number(formData.workProvince));
    const selectedLocality = workLocalities.find(l => l.id === Number(formData.workLocality));

    // Crear el objeto del empleo (nuevo o editado)
    const employmentData = {
      id: isEditingEmployment ? `edited_${editingEmploymentData.id}_${Date.now()}` : `new_${Date.now()}`,
      employer: {
        name: formData.companyName,
        cuit: String(formData.companyIdentification || '').replace(/\D/g, ''),
        phone: formData.workPhone || ''
      },
      job: {
        title: formData.position || '',
        entry_date: formData.entryDate
      },
      address: {
        street: formData.workAddress,
        province: selectedProvince?.id || '',
        province_label: selectedProvince?.name || '',
        locality: selectedLocality?.id || '',
        locality_label: selectedLocality?.name || '',
        postal_code: formData.workPostalCode
      },
      created_at: new Date().toISOString(),
      // Marcar si es una edición para el badge
      isEdited: isEditingEmployment,
      originalId: isEditingEmployment ? editingEmploymentData.id : null
    };

    // Si estamos editando, marcar el empleo original como reemplazado
    let updatedSuggestions;
    if (isEditingEmployment) {
      updatedSuggestions = suggestions.map(suggestion => 
        suggestion.id === editingEmploymentData.id 
          ? { ...suggestion, replacedBy: employmentData.id }
          : suggestion
      );
      // Agregar el nuevo empleo editado
      updatedSuggestions = [employmentData, ...updatedSuggestions];
    } else {
      // Para nuevos empleos, simplemente agregar al inicio
      updatedSuggestions = [employmentData, ...suggestions];
    }
    
    // Seleccionar el empleo por defecto
    setSelectedData(prev => ({
      ...prev,
      employment: employmentData.id
    }));

    // Actualizar el contexto con las nuevas sugerencias
    setSuggestedData(prev => ({
      ...prev,
      employment: updatedSuggestions
    }));

    // IMPORTANTE: Actualizar el formData con los valores seleccionados
    handleChange({ target: { name: 'companyName', value: formData.companyName } });
    handleChange({ target: { name: 'companyIdentification', value: String(formData.companyIdentification || '').replace(/\D/g, '') } });
    handleChange({ target: { name: 'position', value: formData.position || '' } });
    handleChange({ target: { name: 'entryDate', value: formData.entryDate } });
    handleChange({ target: { name: 'workAddress', value: formData.workAddress } });
    handleChange({ target: { name: 'workProvince', value: selectedProvince?.id || '' } });
    handleChange({ target: { name: 'workLocality', value: selectedLocality?.id || '' } });
    handleChange({ target: { name: 'workPostalCode', value: formData.workPostalCode } });

    // Cerrar el modal
    setShowNewEmploymentModal(false);
    setIsEditingEmployment(false);
    setEditingEmploymentData(null);

    // Limpiar el formulario
    setErrors({});
    setTouchedFields({});
    setWorkLocalities([]);

    // Mostrar mensaje de éxito
    Swal.fire({
      icon: 'success',
      title: isEditingEmployment ? 'Empleo editado' : 'Empleo agregado',
      text: isEditingEmployment ? 'La información laboral ha sido editada correctamente.' : 'La nueva información laboral ha sido agregada correctamente.',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const validateSection = () => {
    if (isTargetEmploymentLocked && (!isEditingEmployment || !showNewEmploymentModal)) {
      const isValid = computeEmploymentSectionValidity({
        formData,
        selectedEmploymentId: targetLaborDataId,
        isTargetInvalid
      });
      setSectionValidity((prev) => ({ ...prev, [sectionName]: isValid }));
      return isValid;
    }

    // Una sugerencia seleccionada solo valida si trae datos laborales mínimos.
    if (selectedData.employment && !isEditingEmployment && !showNewEmploymentModal) {
      const isSelectedEmploymentValid = hasMinimumSelectedEmploymentData({
        companyName: formData.companyName,
        companyIdentification: formData.companyIdentification,
        entryDate: formData.entryDate,
        workAddress: formData.workAddress,
        workProvince: formData.workProvince,
        workLocality: formData.workLocality,
        workPostalCode: formData.workPostalCode
      });
      setSectionValidity((prev) => ({ ...prev, [sectionName]: isSelectedEmploymentValid }));
      return isSelectedEmploymentValid;
    }
    
    // Si estamos editando o en el modal, siempre validar los campos del formulario
    const fieldsToValidate = ['companyName', 'companyIdentification', 'entryDate', 'workAddress', 'workPostalCode', 'workProvince', 'workLocality'];
    const newErrors = {};
    fieldsToValidate.forEach((field) => {
      const value = formData[field];
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    setSectionValidity((prev) => ({ ...prev, [sectionName]: isValid }));
    return isValid;
  };

  useEffect(() => {
    if (!isTargetEmploymentLocked) {
      return;
    }

    if (normalizeEmploymentId(selectedData.employment) !== targetLaborDataId) {
      setSelectedData((prev) => ({ ...prev, employment: targetLaborDataId }));
    }

    const isValid = computeEmploymentSectionValidity({
      formData,
      selectedEmploymentId: targetLaborDataId,
      isTargetInvalid
    });

    setSectionValidity((prev) => ({ ...prev, [sectionName]: isValid }));
  }, [
    isTargetEmploymentLocked,
    selectedData.employment,
    targetLaborDataId,
    isTargetInvalid,
    sectionName,
    formData.companyName,
    formData.companyIdentification,
    formData.entryDate,
    formData.workAddress,
    formData.workProvince,
    formData.workLocality,
    formData.workPostalCode,
    setSelectedData,
    setSectionValidity
  ]);

  const handleProvinceChange = async (e) => {
    const { name, value } = e.target;
    handleChange(e);
    
    // Resetear la localidad y su error cuando cambia la provincia
    handleChange({
      target: { 
        name: 'workLocality',
        value: ''
      }
    });

    // Removemos el estado "tocado" de la localidad y provincia cuando cambian
    setTouchedFields(prev => ({
      ...prev,
      workLocality: false,
      workProvince: false
    }));

    setErrors(prev => ({
      ...prev,
      workLocality: '',
      workProvince: ''
    }));
    
    if (value) {
      await loadLocalitiesByProvince(value);
    } else {
      setWorkLocalities([]);
    }
  };
  
  const handleWorkPhoneChange = (payload) => {
    handleFieldChange({
      target: {
        name: 'workPhone',
        value: payload?.e164 || ''
      }
    });
  };

  const handleWorkPhoneBlur = (payload) => {
    setTouchedFields((prev) => ({
      ...prev,
      workPhone: true
    }));
    handleChange({
      target: {
        name: 'workPhone',
        value: payload?.e164 || ''
      }
    });
  };

  const handleLocalityChange = (e) => {
    const { name, value } = e.target;
    handleFieldChange(e);

    const selectedLocality = workLocalities.find((loc) => loc.id === Number(value));
    if (selectedLocality) {
      handleFieldChange({
        target: { name: 'workPostalCode', value: selectedLocality.postal_code },
      });
      validateField('workPostalCode', selectedLocality.postal_code);
    }
  };

  const getLocalitiesFromCache = (provinceId) => {
    const cachedData = localStorage.getItem(`localities_${provinceId}`);
    return cachedData ? JSON.parse(cachedData) : null;
  };
  
  const saveLocalitiesToCache = (provinceId, localities) => {
    localStorage.setItem(`localities_${provinceId}`, JSON.stringify(localities));
  };

  const loadLocalitiesByProvince = async (provinceId) => {
    if (!provinceId) {
      if (isMountedRef.current) {
        setWorkLocalities([]);
      }
      return [];
    }

    const cachedLocalities = getLocalitiesFromCache(provinceId);
    if (cachedLocalities) {
      if (isMountedRef.current) {
        setWorkLocalities(cachedLocalities);
      }
      return cachedLocalities;
    }

    if (isMountedRef.current) {
      setIsLoadingLocalities(true);
    }
    try {
      const response = await api.get(`/provinces/${provinceId}/localities`);
      const loadedLocalities = response.data || [];

      if (isMountedRef.current) {
        setWorkLocalities(loadedLocalities);
      }
      saveLocalitiesToCache(provinceId, loadedLocalities);
      return loadedLocalities;
    } catch (error) {
      console.error('Error fetching localities:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar las localidades. Por favor, intente nuevamente.',
      });
      return [];
    } finally {
      if (isMountedRef.current) {
        setIsLoadingLocalities(false);
      }
    }
  };

  // Helper function para obtener el nombre legible de la provincia
  const getProvinceName = (provinceValue) => {
    if (!provinceValue) return '';
    // Si ya es un nombre (string), devolverlo
    if (typeof provinceValue === 'string' && isNaN(provinceValue)) {
      return provinceValue;
    }
    // Si es un ID, buscar el nombre
    const province = provinces.find(p => p.id === Number(provinceValue));
    return province ? province.name : provinceValue;
  };

  // Helper function para formatear la ubicación (sin dirección)
  const formatEmploymentLocation = (suggestion) => {
    let locality, province, postalCode;
    
    if (suggestion.id.toString().startsWith('new_') || suggestion.id.toString().startsWith('edited_')) {
      // Para empleos nuevos/editados
      locality = suggestion.address?.locality_label || suggestion.address?.locality || '';
      province = suggestion.address?.province_label || getProvinceName(suggestion.address?.province);
      postalCode = suggestion.address?.postal_code || '';
    } else {
      // Para empleos existentes
      locality = suggestion.employer?.locality || '';
      province = suggestion.employer?.province || '';
      postalCode = suggestion.employer?.postal_code || '';
    }
    
    // Construir el formato: "Localidad, Provincia (CP)"
    const locationParts = [locality, province].filter(part => part && String(part).trim());
    let locationString = locationParts.join(', ');
    
    // Agregar código postal entre paréntesis si existe
    if (postalCode && String(postalCode).trim()) {
      locationString += ` (${postalCode})`;
    }
    
    return locationString;
  };

  // useEffect para manejar la selección de localidad cuando se edita un empleo
  useEffect(() => {
    if (isEditingEmployment && editingEmploymentData && workLocalities.length > 0 && !formData.workLocality) {
      let localityToFind;
      
      // Determinar qué localidad buscar según el tipo de empleo
      if (editingEmploymentData.id.toString().startsWith('new_') || editingEmploymentData.id.toString().startsWith('edited_')) {
        localityToFind = editingEmploymentData.address.locality;
      } else {
        localityToFind = editingEmploymentData.employer.locality;
      }
      
      // Buscar la localidad por ID o nombre
      let localityToSelect = null;
      if (!isNaN(localityToFind)) {
        localityToSelect = workLocalities.find(loc => loc.id === Number(localityToFind));
      } else if (typeof localityToFind === 'string') {
        localityToSelect = workLocalities.find(loc => 
          loc.name && loc.name.toLowerCase() === localityToFind.toLowerCase()
        );
      }
      
      if (localityToSelect) {
        handleChange({ target: { name: 'workLocality', value: localityToSelect.id } });
      }
    }
  }, [workLocalities, isEditingEmployment, editingEmploymentData, formData.workLocality]);

  useImperativeHandle(ref, () => ({
    validateSection
  }));

  const renderSuggestionIncompleteIndicators = (suggestion) => {
    if (!shouldShowSiisaIncompleteBadge(suggestion)) {
      return null;
    }

    const missingLabels = getMissingEmploymentFieldLabels(suggestion);

    return (
      <>
        <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
          <span className="badge bg-warning text-dark" data-testid="siisa-incomplete-badge">
            Incompleto
          </span>
        </div>
        {missingLabels.length > 0 && (
          <small className="text-warning d-block lh-1 mt-1" data-testid="employment-missing-fields-hint">
            Faltan: {missingLabels.join(', ')}
          </small>
        )}
      </>
    );
  };

  const isEditingSiisaEmployment = isEditingEmployment && isSiisaEmploymentSuggestion(editingEmploymentData);

  const isCurrentEmploymentFormIncomplete = isEmploymentDataIncomplete({
    companyName: formData.companyName,
    companyIdentification: formData.companyIdentification,
    entryDate: formData.entryDate,
    workAddress: formData.workAddress,
    workProvince: formData.workProvince,
    workLocality: formData.workLocality,
    workPostalCode: formData.workPostalCode
  });

  const renderEmploymentModalTitle = () => (
    <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
      <span>{isEditingEmployment ? 'Editar Empleo' : 'Nuevo Empleo'}</span>
      {isEditingSiisaEmployment && (
        <>
          <span className="badge bg-secondary" data-testid="modal-siisa-source-badge">SIISA</span>
          {isCurrentEmploymentFormIncomplete && (
            <span className="badge bg-warning text-dark" data-testid="modal-siisa-incomplete-badge">
              Incompleto
            </span>
          )}
        </>
      )}
    </Modal.Title>
  );

  const renderSiisaIncompleteModalNotice = () => {
    if (!isEditingSiisaEmployment || !isCurrentEmploymentFormIncomplete) {
      return null;
    }

    return (
      <div className="alert alert-warning mb-3" role="alert" data-testid="siisa-incomplete-modal-alert">
        Esta información viene de SIISA, pero faltan datos obligatorios para continuar.
        Completá los campos marcados con *.
      </div>
    );
  };

  const renderTargetEmploymentNotice = () => {
    if (!isTargetEmploymentLocked || isTargetInvalid) {
      return null;
    }

    return (
      <div
        className="alert alert-info py-2 employment-target-notice"
        role="note"
        data-testid="target-employment-locked-notice"
      >
        Este es el empleador informado al iniciar la solicitud. Podés completar sus datos, pero no cambiar el CUIT.
      </div>
    );
  };

  const renderTargetInvalidAlert = () => {
    if (!isTargetInvalid) {
      return null;
    }

    return (
      <div className="alert alert-warning py-2 mb-3" role="alert" data-testid="target-employment-missing-data">
        No se pudo identificar el CUIT del empleador informado al iniciar la solicitud. No es posible cambiar el empleador.
      </div>
    );
  };

  const renderEmploymentCardDetails = (suggestion, {
    employerName,
    isNewSuggestion,
    isLockedTargetCard
  }) => {
    const secondaryTextClassName = isLockedTargetCard
      ? 'employment-secondary-text d-block lh-1'
      : 'text-muted d-block lh-1';

    return (
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <p className="mb-1 fw-medium lh-1">{employerName}</p>
          {renderSuggestionIncompleteIndicators(suggestion)}
          {suggestion.job?.title && (
            <small className={`${secondaryTextClassName} mb-1`} style={{ fontSize: '0.75rem' }}>
              <FontAwesomeIcon icon={faUserTie} className="me-1" style={{ fontSize: '0.7rem' }} />
              {suggestion.job.title}
            </small>
          )}
          {formatEmploymentLocation(suggestion) && (
            <small className={secondaryTextClassName} style={{ fontSize: '0.75rem' }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ fontSize: '0.7rem' }} />
              {formatEmploymentLocation(suggestion)}
            </small>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {isNewSuggestion && !isLockedTargetCard && (
            <span className="badge bg-primary">Nuevo</span>
          )}
          {isLockedTargetCard && (
            <span className="badge bg-secondary" data-testid="target-employment-badge">
              Empleador informado
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderEmploymentCard = (suggestion) => {
    const suggestionId = normalizeEmploymentId(suggestion?.id);
    const isSelected = normalizeEmploymentId(selectedData.employment) === suggestionId;
    const isNewSuggestion = suggestionId?.startsWith('new_');
    const isEditedSuggestion = suggestionId?.startsWith('edited_');
    const isLockedTargetCard = isTargetEmploymentLocked
      && suggestionId === normalizeEmploymentId(targetLaborDataId);
    const canEdit = isTargetEmploymentLocked
      ? suggestionId === normalizeEmploymentId(targetLaborDataId)
      : (!isNewSuggestion || isEditedSuggestion);
    const employerName = suggestion.employer?.name || 'Empleador informado';
    const cardDetails = renderEmploymentCardDetails(suggestion, {
      employerName,
      isNewSuggestion,
      isLockedTargetCard
    });

    return (
      <div key={`${suggestion.id}-${suggestion.created_at}`} className="col-12">
        <div
          className={`card suggested-address-card mb-1 position-relative${isLockedTargetCard ? ' employment-card--locked-selected' : ''}`}
          data-testid={isLockedTargetCard ? 'employment-card-locked-selected' : undefined}
        >
          <div className="card-body">
            {isLockedTargetCard ? (
              <div className="d-flex align-items-start gap-2">
                <span
                  className="employment-target-indicator"
                  aria-label="Empleador seleccionado"
                  role="img"
                >
                  <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                </span>
                <div className="w-100">
                  {cardDetails}
                </div>
              </div>
            ) : (
              <div className="form-check d-flex align-items-center">
                <input
                  className="form-check-input mt-0"
                  type="radio"
                  name="employmentSelection"
                  id={`employment-${suggestion.id}`}
                  onChange={() => handleUseExisting(suggestion)}
                  checked={isSelected}
                  style={{ marginTop: 0, marginBottom: 0 }}
                />
                <label
                  className="form-check-label w-100 ms-2"
                  htmlFor={`employment-${suggestion.id}`}
                  style={{ cursor: 'pointer' }}
                >
                  {cardDetails}
                </label>
              </div>
            )}
            {!isLockedTargetCard && (
              <DataSourceBadge dataItem={suggestion} options={{ position: 'top-right' }} />
            )}
            {canEdit && (
              <EditButton
                onEdit={() => handleEditEmployment(suggestion)}
                options={{
                  position: 'bottom-right',
                  variant: isLockedTargetCard ? 'primary' : 'muted'
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmploymentModal = (phoneFieldId) => (
    <Modal show={showNewEmploymentModal} onHide={handleCloseModal} size="lg">
      <Modal.Header closeButton>
        {renderEmploymentModalTitle()}
      </Modal.Header>
      <Modal.Body>
        {renderSiisaIncompleteModalNotice()}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Empresa<sup>*</sup></label>
            <input
              type="text"
              className={`form-control ${(errors.companyName && (formSubmitted || touchedFields.companyName)) ? 'is-invalid' : ''}`}
              name="companyName"
              value={formData.companyName || ''}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              required
              autoComplete="off"
            />
            {(errors.companyName && (formSubmitted || touchedFields.companyName)) && <div className="invalid-feedback">{errors.companyName}</div>}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">CUIT Empleador<sup>*</sup></label>
            <input
              type="text"
              inputMode="numeric"
              className={`form-control ${(errors.companyIdentification && (formSubmitted || touchedFields.companyIdentification)) ? 'is-invalid' : ''}`}
              name="companyIdentification"
              value={formData.companyIdentification || ''}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              autoComplete="off"
              required
              readOnly={isTargetEmploymentLocked}
            />
            {(errors.companyIdentification && (formSubmitted || touchedFields.companyIdentification)) && <div className="invalid-feedback">{errors.companyIdentification}</div>}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Fecha de Ingreso<sup>*</sup></label>
            <input
              type="date"
              className={`form-control ${(errors.entryDate && (formSubmitted || touchedFields.entryDate)) ? 'is-invalid' : ''}`}
              name="entryDate"
              value={formData.entryDate || ''}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              autoComplete="off"
              required
            />
            {(errors.entryDate && (formSubmitted || touchedFields.entryDate)) && <div className="invalid-feedback">{errors.entryDate}</div>}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Cargo</label>
            <input
              type="text"
              className="form-control"
              name="position"
              value={formData.position || ''}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              autoComplete="off"
            />
          </div>
          <div className="col-md-6 mb-3">
            <PhoneField
              id={phoneFieldId}
              name="workPhone"
              label="Teléfono Laboral"
              placeholder="Ej: 11 2345 6789"
              defaultCountry="AR"
              valueE164={formData.workPhone || ''}
              onChange={handleWorkPhoneChange}
              onBlur={handleWorkPhoneBlur}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Domicilio Laboral<sup>*</sup></label>
            <input
              type="text"
              className={`form-control ${(errors.workAddress && (formSubmitted || touchedFields.workAddress)) ? 'is-invalid' : ''}`}
              name="workAddress"
              value={formData.workAddress || ''}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              autoComplete="off"
              required
            />
            {(errors.workAddress && (formSubmitted || touchedFields.workAddress)) && <div className="invalid-feedback">{errors.workAddress}</div>}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Provincia<sup>*</sup></label>
            <select
              className={`form-control ${(errors.workProvince && (formSubmitted || touchedFields.workProvince)) ? 'is-invalid' : ''}`}
              name="workProvince"
              value={formData.workProvince || ''}
              onChange={handleProvinceChange}
              onBlur={handleFieldBlur}
              autoComplete="off"
              required
            >
              <option value="" disabled>Seleccione una provincia</option>
              {provinces.map((prov) => (
                <option key={prov.id} value={prov.id}>{prov.name}</option>
              ))}
            </select>
            {(errors.workProvince && (formSubmitted || touchedFields.workProvince)) && <div className="invalid-feedback">{errors.workProvince}</div>}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Localidad<sup>*</sup></label>
            <div className="position-relative">
              <select
                className={`form-control ${(errors.workLocality && !isLoadingLocalities && (formSubmitted || touchedFields.workLocality) && workLocalities.length > 0) ? 'is-invalid' : ''}`}
                name="workLocality"
                value={formData.workLocality || ''}
                onChange={handleLocalityChange}
                onBlur={handleFieldBlur}
                autoComplete="off"
                required
                disabled={!formData.workProvince || isLoadingLocalities}
              >
                <option value="" disabled>
                  {!formData.workProvince
                    ? 'Primero seleccione una provincia'
                    : isLoadingLocalities
                      ? 'Cargando localidades...'
                      : 'Seleccione una localidad'
                  }
                </option>
                {workLocalities.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              {isLoadingLocalities && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </div>
              )}
            </div>
            {(errors.workLocality && !isLoadingLocalities && (formSubmitted || touchedFields.workLocality) && workLocalities.length > 0) && (
              <div className="invalid-feedback d-block">{errors.workLocality}</div>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Código Postal Laboral<sup>*</sup></label>
            <input
              type="text"
              className={`form-control ${(errors.workPostalCode && (formSubmitted || touchedFields.workPostalCode)) ? 'is-invalid' : ''}`}
              name="workPostalCode"
              value={formData.workPostalCode || ''}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              autoComplete="off"
              required
            />
            {(errors.workPostalCode && (formSubmitted || touchedFields.workPostalCode)) && <div className="invalid-feedback">{errors.workPostalCode}</div>}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveNewEmployment}>
            {isEditingEmployment ? 'Guardar Cambios' : 'Guardar Empleo'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );

  // Renderizado condicional basado en el modo de visualización
  if (isContinuousMode) {
    return (
      <div className="continuous-section mb-2">
        <div className="section-header mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="d-flex align-items-center mb-0 fw-light rg-request-section__title">
              <FontAwesomeIcon icon={faBriefcase} className="me-2" />
              Información Laboral
              {sectionValidity[sectionName] === true && (
                <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
              )}
              {sectionValidity[sectionName] === false && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
              )}
            </h5>
            {!isTargetEmploymentLocked && (
              <Button
                variant="outlinePrimary"
                size="sm"
                onClick={handleUseNewForm}
              >
                <i className="bi bi-plus-circle me-1" aria-hidden />
                Nuevo Empleo
              </Button>
            )}
          </div>
        </div>
        <div className="section-content">
          {isLoadingSuggestions ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2 text-muted">Cargando empleos sugeridos...</p>
            </div>
          ) : (
            <div className="suggested-data">
              {renderTargetEmploymentNotice()}
              {renderTargetInvalidAlert()}
              <div className="mb-3">
                <h6 className="mb-0">Gestione su información laboral:</h6>
              </div>
              <div className="row g-1">
                {displayedSuggestions.map(renderEmploymentCard)}
                
                <div className="col-12 mt-3 d-flex justify-content-between align-items-center">
                  <div>
                    {!isTargetEmploymentLocked && !showAllSuggestions && uniqueSuggestions.length > displayedSuggestions.length && (
                      <button 
                        className="btn btn-link text-decoration-none"
                        onClick={() => setShowAllSuggestions(true)}
                      >
                        Ver {uniqueSuggestions.length - displayedSuggestions.length} más
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {renderEmploymentModal('employment-work-phone-continuous')}
      </div>
    );
  }

  // Modo accordion (original)
  return (
    <div className="accordion-item">
      <h2 className="accordion-header" id={`heading${sectionName}`}>
        <button
          className={`accordion-button ${activeSection !== sectionName ? 'collapsed' : ''}`}
          type="button"
          onClick={() => handleSectionClick(sectionName)}
        >
          <FontAwesomeIcon icon={faBriefcase} className="me-2" />
          Información Laboral{' '}
          {sectionValidity[sectionName] === true && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          )}
          {sectionValidity[sectionName] === false && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
          )}
        </button>
      </h2>
      <div
        className={`accordion-collapse collapse ${activeSection === sectionName ? 'show' : ''}`}
        aria-labelledby={`heading${sectionName}`}
      >
        <div className="accordion-body">
          {isLoadingSuggestions ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2 text-muted">Cargando empleos sugeridos...</p>
            </div>
          ) : (
            <div className="suggested-data">
              {renderTargetEmploymentNotice()}
              {renderTargetInvalidAlert()}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Gestione su información laboral:</h6>
                {!isTargetEmploymentLocked && (
                  <Button
                    variant="outlinePrimary"
                    size="sm"
                    onClick={handleUseNewForm}
                  >
                    <i className="bi bi-plus-circle me-1" aria-hidden />
                    Nuevo Empleo
                  </Button>
                )}
              </div>
              <div className="row g-1">
                {displayedSuggestions.map(renderEmploymentCard)}
                
                <div className="col-12 mt-3 d-flex justify-content-between align-items-center">
                  <div>
                    {!isTargetEmploymentLocked && !showAllSuggestions && uniqueSuggestions.length > displayedSuggestions.length && (
                      <button 
                        className="btn btn-link text-decoration-none"
                        onClick={() => setShowAllSuggestions(true)}
                      >
                        Ver {uniqueSuggestions.length - displayedSuggestions.length} más
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {renderEmploymentModal('employment-work-phone-accordion')}
    </div>
  );
});

export default EmploymentInfo;



